import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, FileJson, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from '@/lib/api';

const API = API_BASE_URL;

function timestamp() {
  return new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');
}

export default function AdminExport() {
  const [exporting, setExporting] = useState(false);
  const [exported, setExported] = useState(false);
  const [lastExportedAt, setLastExportedAt] = useState(null);

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setLastExportedAt(new Date());
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/export/json`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Eksport mislykkedes');
      downloadBlob(await res.blob(), `molin30-event-${timestamp()}.json`);
      setExported(true);
      setTimeout(() => setExported(false), 3000);
    } catch {
      alert('Eksport mislykkedes');
    } finally {
      setExporting(false);
    }
  };

  const downloadFile = async (path, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Hentning mislykkedes');
      downloadBlob(await res.blob(), filename);
    } catch {
      alert('Hentning mislykkedes');
    }
  };

  const exports = [
    ['/api/export/csv/guests', `molin30-guests-${timestamp()}.csv`, 'Gæster CSV'],
    ['/api/export/csv/rsvps', `molin30-rsvps-${timestamp()}.csv`, 'RSVP CSV'],
    ['/api/export/csv/bar-orders', `molin30-bar-orders-${timestamp()}.csv`, 'Bar CSV'],
    ['/api/export/csv/music-requests', `molin30-music-requests-${timestamp()}.csv`, 'Musik CSV'],
    ['/api/export/csv/dj-queue', `molin30-dj-queue-${timestamp()}.csv`, 'DJ-kø CSV'],
    ['/api/export/table-cards', `molin30-table-cards-${timestamp()}.txt`, 'Bordkort'],
    ['/api/export/qr-sheet', `molin30-qr-sheet-${timestamp()}.html`, 'QR-ark'],
    ['/api/uploads/archive', `molin30-images-${timestamp()}.zip`, 'Billeder ZIP'],
  ];

  return (
    <div className="animate-fade-in" data-testid="admin-export-page">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-50 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Eksport & backup</h1>
      <p className="text-sm text-slate-400 mb-8">Eksportér eventdata til drift, print og sikkerhedskopi. Gæsteprivat chat og indslag eksporteres ikke.</p>

      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-8 text-center max-w-md mx-auto">
        <FileJson className="w-16 h-16 text-blue-400/50 mx-auto mb-4" />
        <h3 className="text-base font-medium text-slate-200 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>molin30-event.json</h3>
        <p className="text-xs text-slate-400 mb-6">Indeholder almindelige eventdata, men ikke gæsteprivat chat eller hemmelige indslag.</p>
        <Button onClick={handleExport} disabled={exporting}
          className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-8 py-3 rounded-md disabled:opacity-50"
          data-testid="export-json-btn">
          {exporting ? 'Eksporterer...' : exported ? (
            <><CheckCircle className="w-4 h-4 mr-2" /> Eksporteret</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Hent JSON</>
          )}
        </Button>
        {lastExportedAt && <p className="text-xs text-slate-500 mt-3">Senest eksporteret kl. {lastExportedAt.toLocaleTimeString('da-DK')}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-3xl mx-auto mt-6">
        {exports.map(([path, filename, label]) => (
          <Button key={path} variant="outline" onClick={() => downloadFile(path, filename)} className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
            <Download className="w-4 h-4 mr-2" /> {label}
          </Button>
        ))}
      </div>
    </div>
  );
}
